import {PersonCollection} from "../../components/os/PersonCollection";
import {useOsText} from "../../components/os/messages";
import{useCallback,useMemo,useState}from"react";
import{useAppI18n}from"../../lib/appI18n";
import{deviceAuthorityApi,securityApi}from"../../lib/api";
import{access,audit,jitGrants,jitRequests,passkeys,policies,recovery,sessions,settings,users,vaults}from"../../lib/normalize";
import{eventLabel,fmtDate,passkeyType,policyFacts,policyTitle,pretty,resourceLabel,settingFacts}from"../../lib/format";
import{useResource}from"../../lib/useResource";
import{unlockSecurityAuthority}from"../../lib/securityAuthority";
import{runLoginCeremony,runPrfCeremony}from"../../lib/webauthn";
import{EmptyState,InlineNotice,LoadingLine}from"../../components/State";
import{
  useAuthMethodLabel,
  useClientClassLabel,
  usePasskeyType,
  useRelativeTime,
  useRpLabel,
  useStatusLabel,
}from"./securityTerminology";
import{relyingPartyKind,sortPasskeys,statusTone}from"./passkeyDetailModel";
import{
  canRevoke,
  deviceView,
  isCurrentSession,
  relativeMinutes,
  revokeEligibility,
  sessionState,
  sortSessions,
  stateTone,
}from"./sessionAttributionModel";
import type{SessionRow}from"../../lib/types";
import{ConfirmAction,DangerAction,PageAction,ResourceList,ResourceTable,SectionHeader,StateText,WorkspaceHeader}from"../../components/Foundation";

function Protected({
 error,
 onUnlocked,
}:{
 error:string|null;
 onUnlocked:()=>void|Promise<void>;
}){
 const[busy,setBusy]=useState(false);
 const[actionError,setActionError]=useState<string|null>(null);

 if(!error)return null;

 const sessionRequired=
  /passkey_session_required|authentication_required/i.test(
   error
  );

 const signIn=async()=>{
  setBusy(true);
  setActionError(null);

  try{
   const options=await deviceAuthorityApi.loginOptions();
   const proof=await runLoginCeremony(options);
   await deviceAuthorityApi.loginVerify(proof);
   await onUnlocked();
  }catch(e){
   setActionError(
    e instanceof Error
     ?e.message
     :"Passkey sign-in failed."
   );
  }finally{
   setBusy(false);
  }
 };

 const unlock=async()=>{
  setBusy(true);
  setActionError(null);

  try{
   await unlockSecurityAuthority();
   await onUnlocked();
  }catch(e){
   setActionError(
    e instanceof Error
     ?e.message
     :"Security Authority unlock failed."
   );
  }finally{
   setBusy(false);
  }
 };

 return <InlineNotice
  tone="attention"
  title={
   sessionRequired
    ?"Passkey session required"
    :"Administrative authority required"
  }
  detail={
   actionError
    ?actionError
    :sessionRequired
     ?"Sign in with an approved passkey before opening Security Administration."
     :"Unlock Security Authority with fresh passkey approval to view this privileged organization data."
  }
  action={
   <button
    type="button"
    className="button secondary"
    disabled={busy}
    onClick={()=>
     void(
      sessionRequired
       ?signIn()
       :unlock()
     )
    }
   >
    {busy
     ?"Waiting for passkey…"
     :sessionRequired
      ?"Sign in with passkey"
      :"Unlock with passkey"}
   </button>
  }
/>;
}

const yn=(v:boolean)=>v?"Yes":"No";

export function UsersView({onOpenPerson}:{onOpenPerson?:(subjectId:string)=>void}){
 const text=useOsText();
 const r=useResource(useCallback(securityApi.users,[]));
 const rows=useMemo(()=>users(r.data),[r.data]);
 const error=r.status==="error"&&!r.data?r.error:null;
 return <div className="page kns-users-page">
  <WorkspaceHeader title={text("people")} description={text("peopleHelp")} actions={<PageAction disabled={r.status==="loading"} onClick={()=>void r.refresh()}>{text("refresh")}</PageAction>}/>
  <Protected error={error} onUnlocked={r.refresh}/>
  {r.status==="loading"&&!r.data?<LoadingLine/>:null}
  {r.data?<PersonCollection rows={rows} stale={r.status==="error"} onOpenPerson={onOpenPerson}/>:null}
 </div>;
}

export function DevicesView({onOpenPasskey}:{onOpenPasskey?:(credentialUuid:string)=>void}){
 const text=useOsText(),i18n=useAppI18n();
 const t=useCallback((k:string):string=>i18n.t(k),[i18n]);
 const statusLabel=useStatusLabel(),passkeyType=usePasskeyType(),rpLabel=useRpLabel();
 const r=useResource(useCallback(securityApi.passkeys,[]));
 const rows=useMemo(()=>sortPasskeys(r.data?.passkeys??[]),[r.data]);
 const error=r.status==="error"&&!r.data?r.error:null;

 return <div className="page kns-users-page passkey-list">
  <WorkspaceHeader
   eyebrow={t("sc.credentialInfo")}
   title={text("passkeys")}
   description={text("peopleHelp")}
   actions={<PageAction disabled={r.status==="loading"} onClick={()=>void r.refresh()}>{text("refresh")}</PageAction>}
  />
  <Protected error={error} onUnlocked={r.refresh}/>
  {r.status==="loading"&&!r.data?<LoadingLine/>:null}
  {rows.length?
   <>
    <ResourceTable label={text("passkeys")}>
     <table className="admin-table pk-table">
      <thead>
       <tr>
        <th>{text("user")}</th>
        <th>{text("passkeys")}</th>
        <th>{text("trust")}</th>
        <th>{t("sc.rpProfile")}</th>
        <th>{t("sc.synced")}</th>
        <th>{t("sc.unlockCapable")}</th>
        <th>{text("verify")}</th>
        <th>{text("status")}</th>
        {onOpenPasskey&&<th><span className="pk-sr">{t("sc.openPasskeyDetail")}</span></th>}
       </tr>
      </thead>
      <tbody>
       {rows.map(x=>
        <tr key={x.credential_uuid}>
         <td>{x.subject_name}</td>
         <td>
          {onOpenPasskey?
           <button type="button" className="pk-link" onClick={()=>onOpenPasskey(x.credential_uuid)}>{x.label}</button>
           :<strong>{x.label}</strong>
          }
         </td>
         <td>{x.trust_group}</td>
         <td><StateText tone={relyingPartyKind(x.rp_profile_id,x.rp_kind)==="legacy"?"attention":"neutral"}>{rpLabel(x.rp_kind)}</StateText></td>
         <td>{x.backed_up?t("sc.yesShort"):t("sc.noShort")}</td>
         <td>{x.unlock_capable?t("sc.yesShort"):t("sc.noShort")}</td>
         <td>{fmtDate(x.last_used_at)}</td>
         <td><StateText tone={statusTone(x.status)}>{statusLabel(x.status)}</StateText></td>
         {onOpenPasskey&&(
          <td>
           <button
            type="button"
            className="os-detail-button pk-row-action"
            title={t("sc.renamePasskeyHelp")}
            aria-label={`${t("sc.openPasskeyDetail")}: ${x.label}`}
            onClick={()=>onOpenPasskey(x.credential_uuid)}
           >
            <span>{t("sc.openPasskeyDetail")}</span>
            <span aria-hidden="true">›</span>
           </button>
          </td>
         )}
        </tr>
       )}
      </tbody>
     </table>
    </ResourceTable>

    <div className="pk-cards" role="region" aria-label={text("passkeys")}>
     {rows.map(x=>
      <article className="pk-card" key={x.credential_uuid}>
       <div className="pk-card-main">
        <strong>{x.label}</strong>
        <span className="person-meta">
         <span>{x.subject_name}</span>
         <span>{passkeyType(x.device_type,x.backed_up)}</span>
        </span>
       </div>
       <dl className="pk-card-facts">
        <div><dt>{text("status")}</dt><dd><StateText tone={statusTone(x.status)}>{statusLabel(x.status)}</StateText></dd></div>
        <div><dt>{t("sc.backedUp")}</dt><dd>{x.backed_up?t("sc.yesShort"):t("sc.noShort")}</dd></div>
        <div><dt>{t("sc.unlockCapable")}</dt><dd>{x.unlock_capable?t("sc.yesShort"):t("sc.noShort")}</dd></div>
        <div><dt>{text("trust")}</dt><dd>{x.trust_group}</dd></div>
        <div><dt>{t("sc.rpProfile")}</dt><dd>{rpLabel(x.rp_kind)}</dd></div>
        <div><dt>{t("sc.lastUsed")}</dt><dd>{fmtDate(x.last_used_at)}</dd></div>
       </dl>
       {onOpenPasskey&&(
        <button
         type="button"
         className="os-detail-button pk-card-action"
         aria-label={`${t("sc.openPasskeyDetail")}: ${x.label}`}
         onClick={()=>onOpenPasskey(x.credential_uuid)}
        >
         <span>{t("sc.openPasskeyDetail")}</span>
         <span aria-hidden="true">›</span>
        </button>
       )}
      </article>
     )}
    </div>
   </>
   :r.status==="success"&&
    <EmptyState
     title={t("sc.noPasskeysRecorded")}
     detail={t("sc.noPasskeysRecordedDesc")}
    />
  }
 </div>;
}export function VaultsView({onOpenVault}:{onOpenVault:()=>void}){
 const r=useResource(useCallback(securityApi.vaults,[]));
 const rows=useMemo(()=>vaults(r.data),[r.data]);
 const error=r.status==="error"&&!r.data?r.error:null;

 return <div className="page">
  <WorkspaceHeader
   eyebrow="Security Administration"
   title="Vaults"
   description="Organization-vault administration. Personal Vault remains deliberately isolated."
   actions={
    <>
     {r.status==="loading"?<LoadingLine/>:null}
     <PageAction
      type="button"
      onClick={onOpenVault}
      aria-label="Open My Vault"
     >
      Open My Vault
     </PageAction>
    </>
   }
  />

  <Protected error={error} onUnlocked={r.refresh}/>

  {rows.length?
   <ResourceTable label="Organization vaults">
    <table className="admin-table">
     <thead>
      <tr>
       <th>Kind</th>
       <th>Status</th>
       <th>My permissions</th>
       <th>Accessible</th>
      </tr>
     </thead>

     <tbody>
      {rows.map(x=>
       <tr key={x.id}>
        <td>
         <strong>{x.vault_kind}</strong>
        </td>

        <td>
         <StateText
          tone={
           /active|ready|enabled|success/i.test(x.status)
            ?"positive"
            :/critical|failed|revoked|blocked|error/i.test(x.status)
             ?"critical"
             :/risk|warning|pending|missing/i.test(x.status)
              ?"attention"
              :"neutral"
          }
         >
          {x.status}
         </StateText>
        </td>

        <td>
         {String(
          x.current_subject_permissions
         )}
        </td>

        <td>
         <StateText
          tone={
           x.accessible
            ?"positive"
            :"neutral"
          }
         >
          {x.accessible
           ?"Accessible"
           :"Not accessible"}
         </StateText>
        </td>
       </tr>
      )}
     </tbody>
    </table>
   </ResourceTable>
   :r.status==="success"&&
    <EmptyState
     title="No organization vaults"
     detail="No organization vault containers are configured. Personal Vault is not listed here."
    />
  }
 </div>
}

export function AccessView(){
 const r=useResource(useCallback(securityApi.access,[]));
 const rows=useMemo(()=>access(r.data),[r.data]);
 const error=r.status==="error"&&!r.data?r.error:null;

 return <div className="page">
  <WorkspaceHeader
   eyebrow="Security Administration"
   title="Access"
   description="Static, temporary and effective system access in one resource view."
   actions={r.status==="loading"?<LoadingLine/>:undefined}
  />

  <Protected error={error} onUnlocked={r.refresh}/>

  {rows.length?
   <ResourceTable label="Organization access state">
    <table className="admin-table">
     <thead>
      <tr>
       <th>User</th>
       <th>Role</th>
       <th>Static</th>
       <th>JIT</th>
       <th>Effective</th>
       <th>System key</th>
       <th>JIT expiry</th>
      </tr>
     </thead>

     <tbody>
      {rows.map((x,i)=>
       <tr key={x.subject_id||`${x.subject_name}-${i}`}>
        <td>
         <strong>{x.subject_name}</strong>
        </td>

        <td>{x.organization_role}</td>

        <td>{x.static_system_level}</td>

        <td>
         {x.active_jit_level||"—"}
        </td>

        <td>
         {x.effective_system_level}
        </td>

        <td>
         <StateText
          tone={
           x.system_key_ready
            ?"positive"
            :"neutral"
          }
         >
          {x.system_key_ready
           ?"READY"
           :"NONE"}
         </StateText>
        </td>

        <td>
         {fmtDate(
          x.active_jit_expires_at
         )}
        </td>
       </tr>
      )}
     </tbody>
    </table>
   </ResourceTable>
   :r.status==="success"&&
    <EmptyState
     title="No active subjects"
     detail="No effective organization access rows are currently available."
    />
  }
 </div>
}

export function RequestsView(){
 const r=useResource(useCallback(securityApi.requests,[]));
 const pending=useMemo(()=>jitRequests(r.data),[r.data]);
 const grants=useMemo(()=>jitGrants(r.data),[r.data]);
 const[busy,setBusy]=useState<string|null>(null);
 const[msg,setMsg]=useState<string|null>(null);
 const error=r.status==="error"&&!r.data?r.error:null;

 async function decide(id:string,d:"APPROVE"|"DENY"){
  if(!window.confirm(
   `${d==="APPROVE"?"Approve":"Deny"} this privileged access request?`
  ))return;

  setBusy(id+d);
  setMsg(null);

  try{
   await securityApi.decideRequest(id,d);
   setMsg(
    `Request ${d==="APPROVE"?"approved":"denied"}.`
   );
   await r.refresh();
  }catch(e){
   setMsg(
    e instanceof Error
     ?e.message
     :"Decision failed."
   );
  }finally{
   setBusy(null);
  }
 }

 return <div className="page">
  <WorkspaceHeader
   eyebrow="Security Administration"
   title="Requests"
   description="Pending JIT decisions and currently active temporary grants."
   actions={r.status==="loading"?<LoadingLine/>:undefined}
  />

  <Protected error={error} onUnlocked={r.refresh}/>

  {msg&&
   <InlineNotice
    tone={
     /failed|denied|error/i.test(msg)
      ?"attention"
      :"neutral"
    }
    title="Request decision"
    detail={msg}
   />
  }

  <section className="admin-section">
   <SectionHeader
    title={`${pending.length} request${pending.length===1?"":"s"}`}
    description="Pending privileged-access requests awaiting an explicit administrator decision."
   />

   {pending.length?
    <ResourceTable label="Pending privileged access requests">
     <table className="admin-table">
      <thead>
       <tr>
        <th>User</th>
        <th>Requested</th>
        <th>Requested at</th>
        <th>Requested expiry</th>
        <th>Decision</th>
       </tr>
      </thead>

      <tbody>
       {pending.map(x=>
        <tr key={x.request_id}>
         <td>
          <strong>{x.subject_name}</strong>
         </td>

         <td>{x.requested_level}</td>

         <td>
          {fmtDate(
           x.requested_at
          )}
         </td>

         <td>
          {fmtDate(
           x.expires_requested_at
          )}
         </td>

         <td>
          <div className="decision-actions">
           <ConfirmAction
            disabled={busy!==null}
            onClick={()=>
             void decide(
              x.request_id,
              "APPROVE"
             )
            }
            aria-label={`Approve privileged access request for ${x.subject_name}`}
           >
            Approve
           </ConfirmAction>

           <DangerAction
            disabled={busy!==null}
            onClick={()=>
             void decide(
              x.request_id,
              "DENY"
             )
            }
            aria-label={`Deny privileged access request for ${x.subject_name}`}
           >
            Deny
           </DangerAction>
          </div>
         </td>
        </tr>
       )}
      </tbody>
     </table>
    </ResourceTable>
    :r.status==="success"&&
     <p className="quiet-copy">
      No pending privileged access requests.
     </p>
   }
  </section>

  <section className="admin-section">
   <SectionHeader
    title={`${grants.length} temporary grant${grants.length===1?"":"s"}`}
    description="Currently active JIT grants. A temporary grant changes authorization state; it does not create cryptographic key possession."
   />

   {grants.length?
    <ResourceTable label="Active temporary access grants">
     <table className="admin-table">
      <thead>
       <tr>
        <th>User</th>
        <th>Resource</th>
        <th>Level</th>
        <th>Expires</th>
       </tr>
      </thead>

      <tbody>
       {grants.map((x,i)=>
        <tr key={x.grant_id||i}>
         <td>{x.subject_name}</td>
         <td>{x.resource_type}</td>
         <td>{x.access_level}</td>
         <td>
          {fmtDate(
           x.expires_at
          )}
         </td>
        </tr>
       )}
      </tbody>
     </table>
    </ResourceTable>
    :<p className="quiet-copy">
      No active JIT grants.
     </p>
   }
  </section>
 </div>
}

export function SessionsView(){
 const text=useOsText();
 const i18n=useAppI18n();
 const t=useCallback((k:string):string=>i18n.t(k),[i18n]);
 const statusLabel=useStatusLabel(),authLabel=useAuthMethodLabel(),clientLabel=useClientClassLabel();
 const relativeTime=useRelativeTime();
 const r=useResource(useCallback(securityApi.sessions,[]));
 const currentId=r.data?.current_session_id??null;
 const rows=useMemo(()=>sortSessions(sessions(r.data)),[r.data]);
 const error=r.status==="error"&&!r.data?r.error:null;

 const[busy,setBusy]=useState<string|null>(null);
 const[notice,setNotice]=useState<{tone:"ok"|"error";text:string}|null>(null);
 const[confirming,setConfirming]=useState<SessionRow|null>(null);

 const now=Date.now();

 const revoke=async(row:SessionRow)=>{
  if(busy||!canRevoke(row))return;
  setBusy(row.session_id);
  setNotice(null);
  try{
   // Ending somebody else's session is privileged: it always requires a
   // fresh passkey / PRF step-up before the request is issued.
   const options=await deviceAuthorityApi.stepUpOptions();
   const proof=await runPrfCeremony(options.ceremony_id,options.publicKey);
   await securityApi.revokeSession(
    row.session_id,
    proof.ceremony_id,
    proof.credential,
    proof.prf_result,
   );
   setConfirming(null);
   setNotice({tone:"ok",text:t("sc.sessionRevokedSuccess")});
   await r.refresh();
  }catch{
   setNotice({tone:"error",text:t("sc.sessionRevokeFailed")});
  }finally{
   setBusy(null);
  }
 };

 const revokeButton=(row:SessionRow)=>{
  const eligibility=revokeEligibility(row);
  if(eligibility==="current"){
   return <span className="pk-hint">{t("sc.sessionRevokeCurrent")}</span>;
  }
  if(eligibility==="inactive"){
   return <span className="pk-hint">{t("sc.sessionRevokeInactive")}</span>;
  }
  if(eligibility==="unknown")return null;
  return <button
   type="button"
   className="button danger pk-action"
   disabled={Boolean(busy)}
   onClick={()=>setConfirming(row)}
  >
   {t("sc.sessionRevoke")}
  </button>;
 };

 return <div className="page session-list">
  <WorkspaceHeader
   eyebrow={t("sc.sessionsTitle")}
   title={t("sc.sessionsTitle")}
   description={t("sc.sessionsDesc")}
   actions={<PageAction disabled={r.status==="loading"} onClick={()=>void r.refresh()}>{text("refresh")}</PageAction>}
  />

  <Protected error={error} onUnlocked={r.refresh}/>

  {notice&&<div className={`pk-notice ${notice.tone}`} role="status">{notice.text}</div>}

  {confirming&&(
   <div className="pk-confirm" role="alertdialog" aria-labelledby="session-revoke-title">
    <strong id="session-revoke-title">{t("sc.sessionRevokeConfirm")}</strong>
    <p>{t("sc.sessionRevokeHelp")}</p>
    <p className="pk-hint">{t("sc.sessionRequiresStepUp")}</p>
    <div className="pk-actions">
     <button
      type="button"
      className="button danger pk-action"
      disabled={Boolean(busy)}
      onClick={()=>void revoke(confirming)}
     >
      {busy?t("sc.waitingForPasskey"):t("sc.sessionRevoke")}
     </button>
     <button
      type="button"
      className="button secondary pk-action"
      disabled={Boolean(busy)}
      onClick={()=>setConfirming(null)}
     >
      {text("close")}
     </button>
    </div>
   </div>
  )}

  {r.status==="loading"&&!r.data?<LoadingLine/>:null}

  {rows.length?
   <>
    <ResourceTable label={t("sc.sessionsTitle")}>
     <table className="admin-table pk-table">
      <thead>
       <tr>
        <th>{t("sc.personSummary")}</th>
        <th>{t("sc.sessionDevice")}</th>
        <th>{t("sc.sessionAuthMethod")}</th>
        <th>{t("sc.sessionCreated")}</th>
        <th>{t("sc.sessionLastActive")}</th>
        <th>{t("sc.sessionExpires")}</th>
        <th>{t("sc.sessionState")}</th>
        <th><span className="pk-sr">{t("sc.sessionRevoke")}</span></th>
       </tr>
      </thead>
      <tbody>
       {rows.map(x=>{
        const state=sessionState(x);
        const view=deviceView(x);
        const minutes=relativeMinutes(x.last_seen_at,now);
        return <tr key={x.session_id||x.id}>
         <td><strong>{x.subject_name}</strong></td>
         <td>
          <strong>{view.descriptor||t("sc.sessionUnknownDevice")}</strong>
          <span className="person-meta">
           <span>{clientLabel(x.client_class)}</span>
           {view.deviceLabel&&<span>{view.deviceLabel}</span>}
          </span>
         </td>
         <td>{authLabel(x.auth_method)}</td>
         <td>{fmtDate(x.created_at)}</td>
         <td title={fmtDate(x.last_seen_at)}>{relativeTime(minutes)}</td>
         <td>{fmtDate(x.expires_at)}</td>
         <td>
          <StateText tone={stateTone(state)}>{statusLabel(state)}</StateText>
          {isCurrentSession(x,currentId)
           ?<span className="current-badge">{t("sc.currentSessionBadge")}</span>
           :null}
         </td>
         <td>{revokeButton(x)}</td>
        </tr>;
       })}
      </tbody>
     </table>
    </ResourceTable>

    <div className="pk-cards" role="region" aria-label={t("sc.sessionsTitle")}>
     {rows.map(x=>{
      const state=sessionState(x);
      const view=deviceView(x);
      const minutes=relativeMinutes(x.last_seen_at,now);
      const current=isCurrentSession(x,currentId);
      return <article className="pk-card" key={x.session_id||x.id}>
       <div className="pk-card-main">
        <strong>{view.descriptor||t("sc.sessionUnknownDevice")}</strong>
        <span className="person-meta">
         <span>{x.subject_name}</span>
         <span>{authLabel(x.auth_method)}</span>
        </span>
       </div>
       {current&&<span className="current-badge">{t("sc.currentSessionBadge")}</span>}
       <dl className="pk-card-facts">
        <div>
         <dt>{t("sc.sessionState")}</dt>
         <dd><StateText tone={stateTone(state)}>{statusLabel(state)}</StateText></dd>
        </div>
        <div><dt>{t("sc.sessionDevice")}</dt><dd>{clientLabel(x.client_class)}</dd></div>
        <div><dt>{t("sc.sessionLastActive")}</dt><dd>{relativeTime(minutes)}</dd></div>
        <div><dt>{t("sc.sessionExpires")}</dt><dd>{fmtDate(x.expires_at)}</dd></div>
        <div><dt>{t("sc.sessionCreated")}</dt><dd>{fmtDate(x.created_at)}</dd></div>
       </dl>
       <details className="os-technical">
        <summary>{t("sc.sessionTechnical")}</summary>
        <div className="os-technical-body">
         <p className="pk-hint">{t("sc.sessionTechnicalHelp")}</p>
         <dl className="person-facts">
          <div><dt>{t("sc.sessionOs")}</dt><dd>{x.os_family||t("sc.sessionUnknownOs")}</dd></div>
          <div><dt>{t("sc.sessionBrowser")}</dt><dd>{x.browser_family||t("sc.sessionUnknownBrowser")}</dd></div>
          <div><dt>{t("sc.sessionNetwork")}</dt><dd>{x.ip_address||"—"}</dd></div>
          <div><dt>{t("sc.sessionPosture")}</dt><dd>{x.posture_trust_state||"—"}</dd></div>
         </dl>
        </div>
       </details>
       {revokeButton(x)}
      </article>;
     })}
    </div>
   </>
   :r.status==="success"&&
    <EmptyState
     title={t("sc.sessionNoSessions")}
     detail={t("sc.sessionNoSessionsDesc")}
    />
  }
 </div>;
}export function RecoveryView(){
 const r=useResource(useCallback(securityApi.recovery,[]));
 const rows=useMemo(()=>recovery(r.data),[r.data]);
 const error=r.status==="error"&&!r.data?r.error:null;

 return <div className="page">
  <WorkspaceHeader
   eyebrow="Security Administration"
   title="Recovery"
   description="Vault-unlocking passkeys, independent recovery methods and offline recovery readiness."
   actions={r.status==="loading"?<LoadingLine/>:undefined}
  />

  <Protected error={error} onUnlocked={r.refresh}/>

  {rows.length?
   <ResourceTable label="Recovery posture">
    <table className="admin-table">
     <thead>
      <tr>
       <th>User</th>
       <th>Vault-unlocking passkeys</th>
       <th>Independent methods</th>
       <th>Offline recovery</th>
       <th>Overall</th>
       <th>Protection checks</th>
      </tr>
     </thead>

     <tbody>
      {rows.map((x,i)=>{
       const failed=
        Number(x.below_unlock_minimum)
        +Number(x.below_trust_group_minimum);

       const overall=
        x.recovery_state==="DEGRADED"
         ?"NEEDS ATTENTION"
         :x.recovery_state;

       return <tr key={x.subject_id||i}>
        <td>
         <strong>{x.subject_name}</strong>
        </td>

        <td>{x.unlock_credentials}</td>

        <td>{x.trust_groups}</td>

        <td>
         <StateText
          tone={
           x.offline_recovery_ready
            ?"positive"
            :"attention"
          }
         >
          {x.offline_recovery_ready
           ?"READY"
           :"MISSING"}
         </StateText>
        </td>

        <td>
         <StateText
          tone={
           /ready|healthy|protected/i.test(overall)
            ?"positive"
            :/needs attention|degraded|warning|missing/i.test(overall)
             ?"attention"
             :/failed|critical|blocked|error/i.test(overall)
              ?"critical"
              :"neutral"
          }
         >
          {overall}
         </StateText>
        </td>

        <td>
         {failed
          ?`${failed} check${failed===1?"":"s"} below minimum`
          :"Meets minimums"}
        </td>
       </tr>
      })}
     </tbody>
    </table>
   </ResourceTable>
   :r.status==="success"&&
    <EmptyState
     title="No recovery subjects"
     detail="No recovery posture rows are available."
    />
  }
 </div>
}

export function AuditView(){
 const r=useResource(useCallback(securityApi.audit,[]));
 const rows=useMemo(()=>audit(r.data),[r.data]);
 const error=r.status==="error"&&!r.data?r.error:null;

 return <div className="page">
  <WorkspaceHeader
   eyebrow="Security Administration"
   title="Audit"
   description="Canonical security history presented for operational review."
   actions={r.status==="loading"?<LoadingLine/>:undefined}
  />

  <Protected error={error} onUnlocked={r.refresh}/>

  {rows.length?
   <ResourceTable label="Security audit history">
    <table className="admin-table">
     <thead>
      <tr>
       <th>Time</th>
       <th>Event</th>
       <th>Resource</th>
       <th>Outcome</th>
       <th>Significance</th>
      </tr>
     </thead>

     <tbody>
      {rows.map((x,i)=>{
       const severity=String(
        x.severity??""
       );

       return <tr key={x.id||i}>
        <td>
         {fmtDate(
          x.occurred_at
         )}
        </td>

        <td>
         <strong title={x.event_type}>
          {eventLabel(
           x.event_type
          )}
         </strong>
        </td>

        <td
         title={
          [
           x.resource_type,
           x.resource_id
          ]
           .filter(Boolean)
           .join(" ")
         }
        >
         {resourceLabel(
          x.resource_type,
          x.resource_id
         )}
        </td>

        <td>
         {x.outcome}
        </td>

        <td>
         <StateText
          tone={
           /critical|failed|revoked|blocked|error/i.test(severity)
            ?"critical"
            :/risk|warning|pending|missing|needs attention/i.test(severity)
             ?"attention"
             :/healthy|active|ready|enabled|success/i.test(severity)
              ?"positive"
              :"neutral"
          }
         >
          {severity}
         </StateText>
        </td>
       </tr>
      })}
     </tbody>
    </table>
   </ResourceTable>
   :r.status==="success"&&
    <EmptyState
     title="No security events"
     detail="No security audit events are recorded yet."
    />
  }
 </div>
}

export function PoliciesView(){
 const r=useResource(useCallback(securityApi.policies,[]));
 const rows=useMemo(()=>policies(r.data),[r.data]);
 const error=r.status==="error"&&!r.data?r.error:null;

 return <div className="page">
  <WorkspaceHeader
   eyebrow="Security Administration"
   title="Policies"
   description="Security policy state plus the cryptographic trust invariants behind administration."
   actions={r.status==="loading"?<LoadingLine/>:undefined}
  />

  <Protected error={error} onUnlocked={r.refresh}/>

  {rows.length?
   <ResourceList label="Security policies">
    <div className="policy-list">
     {rows.map(x=>
      <article
       className="policy-row"
       key={x.policy_key}
      >
       <div className="policy-main">
        <div>
         <strong>
          {policyTitle(
           x.policy_key
          )}
         </strong>

         <span>
          Version {String(x.policy_version)}
          {" · "}
          <StateText
           tone={
            x.enabled
             ?"positive"
             :"neutral"
           }
          >
           {x.enabled
            ?"Enabled"
            :"Disabled"}
          </StateText>
         </span>
        </div>

        <ul>
         {policyFacts(
          x.policy_key,
          x.config
         ).map(f=>
          <li key={f}>
           {f}
          </li>
         )}
        </ul>
       </div>

       <details>
        <summary>
         Raw policy
        </summary>

        <pre className="inline-code">
         {pretty(
          x.config
         )}
        </pre>
       </details>
      </article>
     )}
    </div>
   </ResourceList>
   :r.status==="success"&&
    <EmptyState
     title="No policies"
     detail="No security policies are currently returned."
    />
  }

  <details className="trust-details">
   <summary>
    Trust model and key-boundary invariants
   </summary>

   <div className="trust-model">
    <p>
     <strong>
      Admin role ≠ Personal Vault decryption.
     </strong>
     {" "}
     Administrative role does not grant access to a user's Personal Vault key.
    </p>

    <p>
     <strong>
      JIT grant ≠ new key slot.
     </strong>
     {" "}
     Temporary authorization does not manufacture cryptographic possession.
    </p>

    <p>
     <strong>
      JIT elevation requires an existing resource key relationship.
     </strong>
     {" "}
     A subject may be elevated only where the resource key boundary already permits it.
    </p>
   </div>
  </details>
 </div>
}

export function SettingsView(){
 const r=useResource(useCallback(securityApi.settings,[]));
 const rows=useMemo(()=>settings(r.data),[r.data]);
 const error=r.status==="error"&&!r.data?r.error:null;

 return <div className="page">
  <WorkspaceHeader
   eyebrow="Security Administration"
   title="Settings"
   description="Effective organization security settings and their source of authority."
   actions={r.status==="loading"?<LoadingLine/>:undefined}
  />

  <Protected error={error} onUnlocked={r.refresh}/>

  {rows.length?
   <ResourceList label="Security settings">
    <div className="policy-list">
     {rows.map(x=>{
      const title=
       x.setting_key==="authority_ttl"
        ?"Administrative authority"
        :x.setting_key.replaceAll("_"," ");

      const source=
       x.source.replaceAll("_"," ");

      return <article
       className="policy-row"
       key={x.setting_key}
      >
       <div className="policy-main">
        <div>
         <strong>
          {title}
         </strong>

         <span>
          Source · {source}
          {" · "}
          <StateText
           tone={
            x.mutable
             ?"attention"
             :"neutral"
           }
          >
           {x.mutable
            ?"Mutable"
            :"Policy controlled"}
          </StateText>
         </span>
        </div>

        <ul>
         {settingFacts(
          x.setting_key,
          x.setting_value
         ).map(f=>
          <li key={f}>
           {f}
          </li>
         )}
        </ul>
       </div>

       <details>
        <summary>
         Raw configuration
        </summary>

        <pre className="inline-code">
         {pretty(
          x.setting_value
         )}
        </pre>
       </details>
      </article>
     })}
    </div>
   </ResourceList>
   :r.status==="success"&&
    <EmptyState
     title="No settings"
     detail="No security settings are currently returned."
    />
  }
 </div>
}
